import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import AppLayout from '@/components/layouts/AppLayout'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import Avatar from '@mui/material/Avatar'
import type { Vendor } from '@/types'

interface Props {
  vendor: Vendor
}

interface PageProps {
  errors?: Record<string, string[]>
}

export default function VendorsEdit({ vendor }: Props) {
  const { props } = usePage<PageProps>()
  const [form, setForm] = useState({
    name: vendor.name,
    description: vendor.description || '',
    website: vendor.website || '',
  })
  const [logo, setLogo] = useState<File | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData()
    formData.append('vendor[name]', form.name)
    formData.append('vendor[description]', form.description)
    formData.append('vendor[website]', form.website)
    if (logo) formData.append('vendor[logo]', logo)
    formData.append('_method', 'patch')
    router.post(`/vendors/${vendor.slug}`, formData)
  }

  const errors = props.errors || {}

  return (
    <AppLayout title="Edit Vendor">
      <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom>Edit Vendor Organization</Typography>
        {vendor.logo_url && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar src={vendor.logo_url} sx={{ width: 64, height: 64 }} />
            <Typography variant="body2" color="text.secondary">Current logo</Typography>
          </Box>
        )}
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name?.join(', ')}
          />
          <TextField
            label="Description"
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <TextField
            label="Website"
            type="url"
            placeholder="https://example.com"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            error={!!errors.website}
            helperText={errors.website?.join(', ')}
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>Update Logo</Typography>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setLogo(e.target.files?.[0] || null)}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={() => router.visit(`/vendors/${vendor.slug}`)}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </Box>
        </Box>
      </Paper>
    </AppLayout>
  )
}
